import React, { useState, useEffect } from 'react';
import { apiKeyService } from '../../../services/whatsapp/apiKeyService';
import { WhatsAppApiKey, WhatsAppWebhook } from '../../../types/models/whatsapp';
import WabaTab from './WabaTab';
import NumbersTab from './NumbersTab';
import NotificationsTab from './NotificationsTab';
import WidgetTab from './WidgetTab';

export default function WhatsAppSettingsPage() {
  const [activeTab, setActiveTab] = useState<'team' | 'apiKeys' | 'webhooks' | 'waba' | 'numbers' | 'notifications' | 'widget'>('team');
  const [loading, setLoading] = useState(false);
  const [members, setMembers] = useState<any[]>([]);
  const [apiKeys, setApiKeys] = useState<WhatsAppApiKey[]>([]);
  const [webhooks, setWebhooks] = useState<WhatsAppWebhook[]>([]);

  const loadData = async () => {
    setLoading(true);
    try {
      if (activeTab === 'team') {
        setMembers(await apiKeyService.listTeamMembers());
      } else if (activeTab === 'apiKeys') {
        setApiKeys(await apiKeyService.listKeys());
      } else if (activeTab === 'webhooks') {
        setWebhooks(await apiKeyService.listWebhooks());
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  return (
    <div className="flex flex-col h-full bg-wa-surface text-wa-text overflow-hidden rounded-lg shadow-sm border border-wa-border">
      <div className="flex items-center space-x-6 p-4 border-b border-wa-border bg-wa-surface2">
        <h2 className="text-lg font-medium text-white">WhatsApp Settings</h2>
        <div className="flex space-x-2">
          {['team', 'apiKeys', 'webhooks', 'waba', 'numbers', 'notifications', 'widget'].map(tab => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab as any)}
              className={`px-3 py-1 text-sm font-medium rounded-md transition-colors ${
                activeTab === tab ? 'bg-wa-green text-gray-900' : 'text-wa-muted hover:text-white hover:bg-wa-surface'
              }`}
            >
              {tab === 'team' ? 'Team Roles' : tab === 'apiKeys' ? 'API Keys' : tab === 'webhooks' ? 'Webhooks' : tab === 'waba' ? 'WABA' : tab === 'numbers' ? 'Numbers' : tab === 'notifications' ? 'Notifications' : 'Widget'}
            </button>
          ))}
        </div>
      </div>
      
      <div className="flex-1 overflow-y-auto p-6">
        {loading && ['team', 'apiKeys', 'webhooks'].includes(activeTab) ? (
          <div className="flex items-center justify-center h-64">
            <div className="w-8 h-8 border-4 border-wa-green border-t-transparent rounded-full animate-spin"></div>
          </div>
        ) : (
          <>
            {activeTab === 'team' && <TeamRolesTab members={members} onUpdate={loadData} />}
            {activeTab === 'apiKeys' && <ApiKeysTab apiKeys={apiKeys} onUpdate={loadData} />}
            {activeTab === 'webhooks' && <WebhooksTab webhooks={webhooks} onUpdate={loadData} />}
            {activeTab === 'waba' && <WabaTab />}
            {activeTab === 'numbers' && <NumbersTab />}
            {activeTab === 'notifications' && <NotificationsTab />}
            {activeTab === 'widget' && <WidgetTab />}
          </>
        )}
      </div>
    </div>
  );
}

function TeamRolesTab({ members, onUpdate }: { members: any[], onUpdate: () => void }) {
  const updateRole = async (id: string, role: string) => {
    try {
      await apiKeyService.updateTeamMemberRole(id, role);
      onUpdate();
    } catch (e) {
      alert('Failed to update role');
    }
  };

  return (
    <div>
      <h3 className="text-white font-medium mb-4">Team Roles</h3>
      <table className="w-full text-left border-collapse">
        <thead>
          <tr className="border-b border-wa-border text-wa-muted text-sm">
            <th className="py-2 px-4">Name</th>
            <th className="py-2 px-4">Email</th>
            <th className="py-2 px-4">Role</th>
          </tr>
        </thead>
        <tbody>
          {members.map(m => (
            <tr key={m.id} className="border-b border-wa-border hover:bg-wa-surface2 transition-colors">
              <td className="py-3 px-4 text-white">{m.name}</td>
              <td className="py-3 px-4 text-wa-muted">{m.email}</td>
              <td className="py-3 px-4">
                <select
                  value={m.whatsappRole}
                  onChange={(e) => updateRole(m.id, e.target.value)}
                  className="bg-wa-surface border border-wa-border rounded text-white p-1"
                >
                  <option value="agent">Agent (Assigned Only)</option>
                  <option value="supervisor">Supervisor (All Chats)</option>
                  <option value="admin">Admin (Full Access)</option>
                </select>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ApiKeysTab({ apiKeys, onUpdate }: { apiKeys: WhatsAppApiKey[], onUpdate: () => void }) {
  const [name, setName] = useState('');
  const [newToken, setNewToken] = useState<{ token: string, name: string } | null>(null);

  const createKey = async () => {
    if (!name.trim()) return;
    try {
      const res = await apiKeyService.createKey(name, ['*']);
      setNewToken({ token: res.token, name });
      setName('');
      onUpdate();
    } catch (e) {
      alert('Failed to create key');
    }
  };

  const revokeKey = async (id: string) => {
    try {
      await apiKeyService.revokeKey(id);
      onUpdate();
    } catch (e) {
      alert('Failed to revoke key');
    }
  };

  return (
    <div>
      <h3 className="text-white font-medium mb-4">API Keys</h3>
      
      {newToken && (
        <div className="bg-wa-surface2 border border-wa-green p-4 rounded mb-6">
          <p className="text-wa-green font-medium mb-2">Key created: {newToken.name}</p>
          <p className="text-white font-mono break-all bg-black p-2 rounded">{newToken.token}</p>
          <p className="text-wa-muted text-sm mt-2">Copy this key now. It will not be shown again.</p>
          <button onClick={() => setNewToken(null)} className="mt-3 text-sm text-wa-muted hover:text-white">Dismiss</button>
        </div>
      )}

      <div className="flex space-x-2 mb-6">
        <input 
          type="text" 
          value={name} 
          onChange={e => setName(e.target.value)}
          placeholder="New Key Name" 
          className="bg-wa-surface2 border border-wa-border rounded px-3 py-1 text-white flex-1"
        />
        <button onClick={createKey} className="bg-wa-green text-gray-900 px-4 py-1 rounded font-medium">Create Key</button>
      </div>

      <table className="w-full text-left border-collapse">
        <thead>
          <tr className="border-b border-wa-border text-wa-muted text-sm">
            <th className="py-2 px-4">Name</th>
            <th className="py-2 px-4">Prefix</th>
            <th className="py-2 px-4">Created</th>
            <th className="py-2 px-4">Status</th>
            <th className="py-2 px-4">Action</th>
          </tr>
        </thead>
        <tbody>
          {apiKeys.map(k => (
            <tr key={k.id} className="border-b border-wa-border hover:bg-wa-surface2 transition-colors">
              <td className="py-3 px-4 text-white">{k.name}</td>
              <td className="py-3 px-4 text-wa-muted font-mono">{k.prefix}</td>
              <td className="py-3 px-4 text-wa-muted">{new Date(k.createdAt).toLocaleDateString()}</td>
              <td className="py-3 px-4">
                {k.revokedAt ? <span className="text-red-400">Revoked</span> : <span className="text-wa-green">Active</span>}
              </td>
              <td className="py-3 px-4">
                {!k.revokedAt && (
                  <button onClick={() => revokeKey(k.id)} className="text-red-400 hover:text-red-300 text-sm">Revoke</button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function WebhooksTab({ webhooks, onUpdate }: { webhooks: WhatsAppWebhook[], onUpdate: () => void }) {
  const [url, setUrl] = useState('');

  const createWebhook = async () => {
    if (!url.trim()) return;
    try {
      await apiKeyService.createWebhook(url, ['*'], '');
      setUrl('');
      onUpdate();
    } catch (e) {
      alert('Failed to create webhook');
    }
  };

  const deleteWebhook = async (id: string) => {
    try {
      await apiKeyService.deleteWebhook(id);
      onUpdate();
    } catch (e) {
      alert('Failed to delete webhook');
    }
  };

  return (
    <div>
      <h3 className="text-white font-medium mb-4">Webhooks</h3>
      
      <div className="flex space-x-2 mb-6">
        <input 
          type="text" 
          value={url} 
          onChange={e => setUrl(e.target.value)}
          placeholder="https://..." 
          className="bg-wa-surface2 border border-wa-border rounded px-3 py-1 text-white flex-1"
        />
        <button onClick={createWebhook} className="bg-wa-green text-gray-900 px-4 py-1 rounded font-medium">Add Webhook</button>
      </div>

      <table className="w-full text-left border-collapse">
        <thead>
          <tr className="border-b border-wa-border text-wa-muted text-sm">
            <th className="py-2 px-4">URL</th>
            <th className="py-2 px-4">Failures</th>
            <th className="py-2 px-4">Status</th>
            <th className="py-2 px-4">Action</th>
          </tr>
        </thead>
        <tbody>
          {webhooks.map(w => (
            <tr key={w.id} className="border-b border-wa-border hover:bg-wa-surface2 transition-colors">
              <td className="py-3 px-4 text-white break-all">{w.url}</td>
              <td className="py-3 px-4 text-wa-muted">{w.failureCount}</td>
              <td className="py-3 px-4">
                {w.active ? <span className="text-wa-green">Active</span> : <span className="text-red-400">Deactivated</span>}
              </td>
              <td className="py-3 px-4">
                <button onClick={() => deleteWebhook(w.id)} className="text-red-400 hover:text-red-300 text-sm">Delete</button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
